
import { Component, Vue, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import httpClient from "../../store/httpClient";
import { CustomerSearchRequestDto } from "../../dtos/customerSearchRequestDto";
import { CustomerSortingType } from "../../dtos/customerSortingType";
import { TagDto } from "@/dtos/tagDto";

@Component
export default class CustomerDashboard extends Vue {
	private loadBatchSize: number = 50;
	private isCustomersLoading: boolean = false;
	private isTotalLoading: boolean = false;
	private currentFilterCustomerCount: number = 0;

	private headers: Array<{
		text: string;
		value: string;
		align: string;
		sortable: boolean;
	}> = Array(
		{
			text: "KndNr",
			value: "customerId",
			align: "left",
			sortable: true,
		},
		{
			text: "Firma",
			value: "companyName",
			align: "left",
			sortable: true,
		},
		{
			text: "Vorname",
			value: "firstname",
			align: "left",
			sortable: false,
		},
		{
			text: "Nachname",
			value: "name",
			align: "left",
			sortable: false,
		},
		{
			text: "E-Mail",
			value: "eMail",
			align: "left",
			sortable: false,
		},
		{
			text: "Land",
			value: "country",
			align: "left",
			sortable: false,
		},
		{
			text: "Registrierung",
			value: "registriertAm",
			align: "right",
			sortable: true,
		},
		{
			text: "Erste Bestellung",
			value: "firstOrderDate",
			align: "right",
			sortable: true,
		},
		{
			text: "Letzte Bestellung",
			value: "lastOrderDate",
			align: "right",
			sortable: true,
		},
		{
			text: "# Artikel",
			value: "numberOfArtikel",
			align: "right",
			sortable: true,
		},
		{
			text: "# Bestellungen",
			value: "numberOfOrders",
			align: "right",
			sortable: true,
		},
		{
			text: "Bestellsumme",
			value: "totalAmountOrdered",
			align: "right",
			sortable: true,
		}
	);

	beforeRouteEnter(to: any, from: any, next: any) {
		next((vm: any) => {
			window.addEventListener("scroll", vm.checkBottom);
		});
	}

	beforeRouteLeave(to: any, from: any, next: any) {
		window.removeEventListener("scroll", this.checkBottom);
		next();
	}

	private mounted() {
		if (this.customers.length === 0) {
			this.loadCustomers(true);
			this.getTotalCustomerCount();
		}
	}

	private customSort(items: any, index: any, isDesc: any) {
		// Do nothing because items are correclty sorted by the backend
		// But a custom sort is needed because otherwise the data-table shuffles items around
		// Maybe there is another solution to stop the sorting but I haven't found it, yet.
		return items;
	}

	private skipRules = [
		(value: number) => value >= 0 || "Muss positiv sein",
		(value: number) => value < this.totalCustomerCount || `Maximal ${this.totalCustomerCount}`,
	];

	private get isLoading() {
		return this.$store.state.isLoadingCustomers;
	}
	private set isLoading(isLoading: boolean) {
		this.$store.commit("setCustomerLoading", isLoading);
	}

	private get customers() {
		return this.$store.state.customers;
	}

	private get totalCustomerCount() {
		return this.$store.state.totalCustomerCount;
	}

	private get searchRequest(): CustomerSearchRequestDto {
		return this.$store.getters.currentCustomerSearchRequest;
	}

	private get searchTerm(): string {
		return this.$store.state.suchbegriff;
	}
	private set searchTerm(searchTerm: string) {
		this.$store.commit("setSuchbegriff", searchTerm);
	}

	private get manualSkip(): number {
		return this.$store.state.manualSkip;
	}
	private set manualSkip(skip: number) {
		this.$store.commit("setManualSkip", skip);
	}

	private get lastSearchTerm(): string {
		return this.$store.state.customerLastSearchTerm;
	}
	private set lastSearchTerm(lastSearchTerm: string) {
		this.$store.commit("setCustomerLastSearchTerm", lastSearchTerm);
	}

	private get hasMoreToLoad(): boolean {
		return this.$store.state.hasMoreCustomersToLoad;
	}
	private set hasMoreToLoad(hasMoreToLoad: boolean) {
		this.$store.commit("setHasMoreCustomersToLoad", hasMoreToLoad);
	}

	private get volltextSuche(): boolean {
		return this.$store.state.customerVolltextSuche;
	}
	private set volltextSuche(isVolltextSuche: boolean) {
		this.$store.commit("setCustomerVolltextSuche", isVolltextSuche);
	}

	private get tags() {
		return this.$store.state.tags;
	}
	private get filterTags(): TagDto[] {
		return this.$store.state.customerFilterTags;
	}
	private set filterTags(tags: TagDto[]) {
		this.$store.commit("setFilterTags", tags);
	}

	private get countries() {
		return this.$store.state.countries;
	}
	private get filterCountries(): String[] {
		return this.$store.state.customerFilterCountries;
	}
	private set filterCountries(countries: String[]) {
		this.$store.commit("setCustomerFilterCountries", countries);
	}

	private async loadCustomers(reload: boolean) {
		if (this.isLoading) {
			return;
		}
		try {
			this.isCustomersLoading = true;
			this.setLoading();
			console.log("loading customers...");
			let from = 0;
			if (this.manualSkip > 0) {
				from = parseInt(this.manualSkip.toString());
			}
			if (!reload) {
				from += this.$store.state.customers.length;
			}
			const response = await httpClient().post(
				`Customer/GetCustomerInfo` + `?from=${from}` + `&to=${from + this.loadBatchSize}`,
				this.searchRequest
			);
			if (reload) {
				this.$store.commit("setCustomers", response.data.customerInfos);
				this.currentFilterCustomerCount = response.data.count;
			} else {
				this.$store.commit("addOrUpdateCustomers", response.data.customerInfos);
			}

			this.hasMoreToLoad = response.data.customerInfos.length === this.loadBatchSize;
		} catch {
			console.log("Load customers failed.");
		} finally {
			this.isCustomersLoading = false;
			this.setLoading();
		}
	}

	private async getTotalCustomerCount() {
		try {
			this.isTotalLoading = true;
			this.setLoading();
			const response = await httpClient().get(`Customer/GetTotalCustomerCount`);
			this.$store.commit("setTotalCustomerCount", response.data);
		} catch {
			console.log("Load total customer count failed.");
		} finally {
			this.isTotalLoading = false;
			this.setLoading();
		}
	}

	private searchOnEnter() {
		if (!this.isLoading) {
			this.lastSearchTerm = this.searchTerm;
			this.loadCustomers(true);
		}
	}

	@debounce(700, { leading: false })
	@Watch("searchTerm")
	private reloadOnSearchTermChange() {
		if (
			!this.isLoading &&
			this.lastSearchTerm !== this.searchTerm &&
			(this.searchTerm.length >= 3 || this.searchTerm.length === 0)
		) {
			this.lastSearchTerm = this.searchTerm;
			this.loadCustomers(true);
		}
	}

	@Watch("volltextSuche")
	private reloadOnVolltextChange() {
		if (!this.isLoading) {
			this.loadCustomers(true);
		}
	}

	@debounce(700, { leading: false })
	@Watch("manualSkip")
	private reloadOnSkipChange() {
		(this.$refs["skip"] as any).validate();
		if ((this.$refs["skip"] as any).valid && !this.isLoading) {
			this.loadCustomers(true);
		}
	}

	private checkBottom() {
		let bottomOfWindow =
			window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 2;
		if (bottomOfWindow && this.hasMoreToLoad && !this.isLoading) {
			this.loadCustomers(false);
		}
	}

	private manuallyLoadMoreCustomers() {
		if (this.hasMoreToLoad && !this.isLoading) {
			this.loadCustomers(false);
		}
	}

	private removeTagFilter(item: any) {
		const index = this.filterTags.indexOf(item);
		this.filterTags.splice(index, 1);
	}

	private navigateTo(customerId: string) {
		this.$router.push({ name: "customerDetail", params: { customerId } });
	}

	private setLoading() {
		this.isLoading = this.isCustomersLoading || this.isTotalLoading;
	}

	private changeSort(column: string, sortable: boolean) {
		if (sortable) {
			let newSort: CustomerSortingType;
			if (this.sortProps.sortByColumn === column) {
				newSort = this.sortProps.sortDescending
					? this.searchRequest.sortingType - 1
					: this.searchRequest.sortingType + 1;
			} else {
				newSort = this.getSortTypeFromColumn(column);
			}
			this.$store.commit("setCustomerSortType", newSort);
			this.loadCustomers(true);
		}
	}

	private get sortProps() {
		let descending: boolean = false;
		let column: string = "registriertAm";
		let currentSortingType: CustomerSortingType = this.$store.state.customerSortingType;
		switch (currentSortingType) {
			case CustomerSortingType.CustomerIdAscending: {
				descending = false;
				column = "customerId";
				break;
			}
			case CustomerSortingType.CustomerIdDescending: {
				descending = true;
				column = "customerId";
				break;
			}
			case CustomerSortingType.FirmaAscending: {
				descending = false;
				column = "companyName";
				break;
			}
			case CustomerSortingType.FirmaDescending: {
				descending = true;
				column = "companyName";
				break;
			}
			case CustomerSortingType.VornameAscending: {
				descending = false;
				column = "firstname";
				break;
			}
			case CustomerSortingType.VornameDescending: {
				descending = true;
				column = "firstname";
				break;
			}
			case CustomerSortingType.NachnameAscending: {
				descending = false;
				column = "name";
				break;
			}
			case CustomerSortingType.NachnameDescending: {
				descending = true;
				column = "name";
				break;
			}
			case CustomerSortingType.RegisteredAscending: {
				descending = false;
				column = "registriertAm";
				break;
			}
			case CustomerSortingType.RegisteredDescending: {
				descending = true;
				column = "registriertAm";
				break;
			}
			case CustomerSortingType.FirstOrderAscending: {
				descending = false;
				column = "firstOrderDate";
				break;
			}
			case CustomerSortingType.FirstOrderDescending: {
				descending = true;
				column = "firstOrderDate";
				break;
			}
			case CustomerSortingType.LastOrderAscending: {
				descending = false;
				column = "lastOrderDate";
				break;
			}
			case CustomerSortingType.LastOrderDescending: {
				descending = true;
				column = "lastOrderDate";
				break;
			}
			case CustomerSortingType.NumberOfArticlesAscending: {
				descending = false;
				column = "numberOfArtikel";
				break;
			}
			case CustomerSortingType.NumberOfArticlesDescending: {
				descending = true;
				column = "numberOfArtikel";
				break;
			}
			case CustomerSortingType.NumberOfOrdersAscending: {
				descending = false;
				column = "numberOfOrders";
				break;
			}
			case CustomerSortingType.NumberOfOrdersDescending: {
				descending = true;
				column = "numberOfOrders";
				break;
			}
			case CustomerSortingType.OrderSumAscending: {
				descending = false;
				column = "totalAmountOrdered";
				break;
			}
			case CustomerSortingType.OrderSumDescending: {
				descending = true;
				column = "totalAmountOrdered";
				break;
			}
		}
		return {
			sortDescending: descending,
			sortByColumn: column,
		};
	}

	getSortTypeFromColumn(column: string) {
		switch (column) {
			case "customerId": {
				return CustomerSortingType.CustomerIdAscending;
			}
			case "companyName": {
				return CustomerSortingType.FirmaAscending;
			}
			case "firstname": {
				return CustomerSortingType.VornameAscending;
			}
			case "name": {
				return CustomerSortingType.NachnameAscending;
			}
			case "registriertAm": {
				return CustomerSortingType.RegisteredAscending;
			}
			case "firstOrderDate": {
				return CustomerSortingType.FirstOrderAscending;
			}
			case "lastOrderDate": {
				return CustomerSortingType.LastOrderAscending;
			}
			case "numberOfArtikel": {
				return CustomerSortingType.NumberOfArticlesAscending;
			}
			case "numberOfOrders": {
				return CustomerSortingType.NumberOfOrdersAscending;
			}
			case "totalAmountOrdered": {
				return CustomerSortingType.OrderSumAscending;
			}
			default: {
				return CustomerSortingType.CustomerIdDescending;
			}
		}
	}
}
